import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { LuLibrary, LuSearch, LuDownload, LuExternalLink, LuFolderOpen } from 'react-icons/lu';
import { errorMessage, fmtDate } from './trainingUtils';
import { materialType, toneStyle, fmtSize, openMaterial } from './supportMaterialsUtils';
import './training.css';

/**
 * Materiais de Apoio — colaborador de empresa mentorada (módulo "Materiais
 * de Apoio" habilitado em "Módulos por Empresa"). Lista só os materiais
 * visíveis, agrupados por categoria, com busca e download.
 */

const OTHERS = 'Outros materiais';

export default function SupportMaterialsPage() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/my-materials');
      setItems(data);
      setError('');
    } catch (e) {
      setError(errorMessage(e, 'Erro ao carregar os materiais'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const categories = useMemo(() => [...new Set(items.map((m) => m.category || OTHERS))]
    .sort((a, b) => (a === OTHERS) - (b === OTHERS) || a.localeCompare(b, 'pt-BR')), [items]);

  const term = search.trim().toLowerCase();
  const visible = items.filter((m) => (!category || (m.category || OTHERS) === category)
    && (!term || `${m.title} ${m.description || ''} ${m.category || ''}`.toLowerCase().includes(term)));

  const groups = categories
    .map((c) => ({ name: c, list: visible.filter((m) => (m.category || OTHERS) === c) }))
    .filter((g) => g.list.length);

  const handleOpen = async (m) => {
    setBusyId(m.id); setError('');
    try { await openMaterial('/my-materials', m); } catch (e) { setError(e.message); } finally { setBusyId(null); }
  };

  return (
    <div className="page-card">
      <div className="page-card-header">
        <div>
          <div className="page-card-title">Materiais de Apoio</div>
          <div className="page-card-subtitle">Arquivos e links da DHR Consultoria para o dia a dia da sua gestão</div>
        </div>
      </div>

      {error && <div className="alert-custom danger">{error}</div>}

      <div className="training-toolbar">
        <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 420 }}>
          <LuSearch style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
          <input className="form-control" style={{ paddingLeft: 32, maxWidth: 'none' }} placeholder="Buscar material"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {categories.length > 1 && (
          <div className="training-chips" style={{ marginBottom: 0 }}>
            <button type="button" className={`training-chip ${!category ? 'is-active' : ''}`} onClick={() => setCategory('')}>Todas</button>
            {categories.map((c) => (
              <button key={c} type="button" className={`training-chip ${category === c ? 'is-active' : ''}`} onClick={() => setCategory(c)}>{c}</button>
            ))}
          </div>
        )}
      </div>

      {loading && !items.length && <div className="training-hint">Carregando…</div>}

      {!loading && !items.length && !error && (
        <div className="empty-state">
          <LuLibrary size={36} />
          <p>Nenhum material disponível ainda</p>
          <small>Quando a DHR Consultoria publicar materiais de apoio, eles aparecem aqui.</small>
        </div>
      )}
      {!loading && items.length > 0 && !groups.length && (
        <div className="empty-state"><p>Nenhum material encontrado</p></div>
      )}

      {groups.map((g) => (
        <section key={g.name} className="material-group">
          <h3 className="material-group-title"><LuFolderOpen /> {g.name} <span>{g.list.length}</span></h3>
          <div className="material-grid">
            {g.list.map((m) => {
              const t = materialType(m);
              return (
                <article key={m.id} className="material-card">
                  <div className="material-card-head">
                    <span className="material-type-icon" style={toneStyle(t.tone)}>{t.icon}</span>
                    <div style={{ minWidth: 0 }}>
                      <div className="material-card-title">{m.title}</div>
                      <div className="training-hint" style={{ marginTop: 2 }}>
                        {t.label}{m.kind === 'file' && m.file_size ? ` · ${fmtSize(m.file_size)}` : ''} · {fmtDate(m.updatedAt)}
                      </div>
                    </div>
                  </div>
                  {m.description && <p className="material-card-desc">{m.description}</p>}
                  <button type="button" className="training-btn training-btn--sm" onClick={() => handleOpen(m)} disabled={busyId === m.id}>
                    {m.kind === 'link' ? <><LuExternalLink /> Abrir link</> : <><LuDownload /> {busyId === m.id ? 'Baixando…' : 'Baixar'}</>}
                  </button>
                </article>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
